import styled from "styled-components";
import colours from "../../lib/colours";
import { Link } from "../../components/atoms/Link";
import { ExternalLinkIcon } from "../../components/atoms/ExternalLinkIcon";
import Callout from "../../components/molecules/Callout";
import { guidelines } from "../../lib/constants";

const Container = styled.section`
  padding-bottom: 24px;
`;

const SectionTitle = styled.h4`
  margin-top: 24px;
  margin-bottom: -8px;
  font-weight: 600;
  color: ${colours.th1};
`;

const ItalicsSpan = styled.span`
  font-style: italic;
`;

interface IProps {
  pathway: string;
}

function ReviewDownloadRemoval({ pathway }: IProps) {
  const detailed = pathway === "Detailed";

  const renderNonDetailedContent = () => {
    return (
      <>
        <p>
          Your proposal to remove native vegetation falls under the{" "}
          <span style={{ fontWeight: 500 }}>{pathway}</span> Assessment Pathway.
          The application must meet all application requirements outlined in
          Table 4 of the{" "}
          <Link
            href={guidelines}
            target="_blank"
            title="Guidelines for the removal, destruction or lopping of native vegetation"
            rel="noopener noreferrer"
          >
            Guidelines for the removal, destruction or lopping of native
            vegetation
          </Link>
          <ExternalLinkIcon /> (the Guidelines).
        </p>
        <p>
          If you have finished mapping your clearing proposal and addressed the
          application requirements on the previous page, proceed with the
          following steps:
        </p>
        <ol>
          <li>
            Consider opportunities to further avoid and minimise impacts on
            native vegetation. If further native vegetation removal can be
            avoided, remap your proposal.
          </li>
          <li>
            Export the mapped native vegetation dataset for your future use.
            This dataset can be uploaded to the system and edited at a later
            date.
          </li>
          <li>Download the Native Vegetation Removal Report.</li>
          <li>
            Ensure you have addressed all application requirements outlined in
            Table 4 of the Guidelines. Assuming you have addressed the
            application requirements on the previous page, the Native Vegetation
            Removal Report will cover most of the application requirements
            outlined in Table 4 of the Guidelines. The following outstanding
            requirements will need to be addressed in your approval application:
            <ul>
              <li>
                Application Requirement 3 - Recent, dated photographs of the
                native vegetation to be removed.
              </li>
              <li>
                Application Requirement 6 – If an approved Property Vegetation
                Plan (PVP) applies to the property, it must be provided with the
                application.
              </li>
            </ul>
          </li>
          <li>
            Prepare your application and apply for approval to remove, destroy
            or lop native vegetation. In most cases, approval will be sought
            from the local Council under Clauses 52.17 or 52.16 of the local
            planning scheme; however, alternative approval provisions may apply.
            Please contact your local Council if you require assistance with
            determining the applicable approvals process.
          </li>
        </ol>
      </>
    );
  };
  const renderDetailedContent = () => {
    return (
      <>
        <p>
          Your proposal to remove native vegetation falls under the{" "}
          <span style={{ fontWeight: 500 }}>Detailed</span> Assessment Pathway.
          The application must meet all application requirements outlined in
          Tables 4 and 5 of the{" "}
          <Link
            href={guidelines}
            target="_blank"
            title="Guidelines for the removal, destruction or lopping of native vegetation"
            rel="noopener noreferrer"
          >
            Guidelines for the removal, destruction or lopping of native
            vegetation
          </Link>
          <ExternalLinkIcon /> (the Guidelines).
        </p>
        <Callout type="warning" hideIcon>
          <span style={{ fontWeight: 500 }}>
            This tool will not generate the report needed for an application to
            remove native vegetation in the Detailed Assessment pathway.
          </span>
        </Callout>
        <p>
          To progress with seeking approval for your proposal, you need to
          appoint a certified native vegetation assessor to:
        </p>
        <ul>
          <li>Map the native vegetation you propose to remove.</li>
          <li>
            Provide advice on how to minimise your impacts, offset requirements
            and costs.
          </li>
          <li>Obtain a Native Vegetation Removal Report from DEECA.</li>
          <li>
            Complete a site assessment report, which may be lodged with the
            application for approval.
          </li>
          <li>Provide advice on how to secure an appropriate offset.</li>
        </ul>
        <p>
          A certified native vegetation assessor may also advise whether you
          need to consider any other legislation, such as the Victorian{" "}
          <ItalicsSpan>Flora and Fauna Guarantee Act 1988</ItalicsSpan> and the
          Commonwealth{" "}
          <ItalicsSpan>
            Environment Protection and Biodiversity Conservation Act 1999
          </ItalicsSpan>
          .
        </p>
        <p>
          Certified native vegetation assessors are listed on the{" "}
          <Link
            href="https://www.environment.vic.gov.au/native-vegetation/native-vegetation-removal-regulations"
            target="_blank"
            title="DEECA website"
            rel="noopener noreferrer"
          >
            DEECA website
          </Link>
          <ExternalLinkIcon /> and will charge a fee for these services.
        </p>
      </>
    );
  };

  return (
    <Container>
      <SectionTitle>Next steps</SectionTitle>
      {detailed ? renderDetailedContent() : renderNonDetailedContent()}
    </Container>
  );
}

export default ReviewDownloadRemoval;
